import { useEffect, useState } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import { useNavigate } from 'react-router-dom';
import { documentId, where } from 'firebase/firestore';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ButtonType } from 'core/enums/button-type';
import { TransportJobsApiService } from 'core/api';
import { ITransportJobDao } from 'core/api/types/transport-job.interface';
import { ITransportGroup } from 'core/api/transport-groups/transport-groups-api-interface';
import { TransportJobStatusLabels } from 'core/constants/transport-job-status';
import { useDialog } from 'core/providers/DialogProvider';
import TransportRunMap from './transport-run-map';

/**
 * How a batch is getting on: every job in it, and where each has got to.
 *
 * The batch's own status says whether somebody has taken it on. It says nothing
 * about whether the third collection has happened, which is what the office is
 * asked on the phone. That answer is the jobs, each with its own status, and
 * each a click away from its own screen — the same click the jobs list gives.
 *
 * Read directly rather than through the batch, because a batch's `stops` are a
 * snapshot of the plan: they say what the driver was asked to do, not what they
 * have done.
 */

interface IProps {
  group: ITransportGroup;
}

/** Firestore takes ten at a time for an `in`, and batches can be larger. */
const CHUNK = 10;

const chunked = <T,>(values: T[], size: number): T[][] =>
  values.reduce<T[][]>((acc, value, index) => {
    if (index % size === 0) acc.push([]);
    acc[acc.length - 1].push(value);
    return acc;
  }, []);

const BatchProgressDialog = ({ group }: IProps) => {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [jobs, setJobs] = useState<ITransportJobDao[]>();

  useEffect(() => {
    const load = async () => {
      const pages = await Promise.all(
        chunked(group.jobUids, CHUNK).map((ids) =>
          TransportJobsApiService.getAll([where(documentId(), 'in', ids)]),
        ),
      );
      setJobs(pages.flatMap((page) => page.docs.map((doc) => doc.data() as ITransportJobDao)));
    };

    load().catch(() => setJobs([]));
  }, [group.jobUids]);

  const openJob = (uid: string) => {
    // The dialog closes first: leaving it open over the job it just navigated
    // to leaves somebody looking at a list they have already left.
    dialog?.closeDialog();
    navigate(`/transport/${uid}`);
  };

  return (
    <SharedDialogBase
      // A batch built before references existed has none, and "undefined — 2
      // jobs from Royal Derby" is worse than just the label.
      title={group.reference ? `${group.reference} — ${group.label}` : group.label}
      customButtons={[
        {
          key: 'close',
          type: ButtonType.Button,
          buttonProps: {
            onClick: () => dialog?.closeDialog(),
            appearance: 'primary',
            label: 'Close',
            type: 'button',
            testId: 'close-batch-progress',
          },
        },
      ]}
      customContentTemplate={
        <div className='max-h-[70vh] w-[52rem] max-w-full overflow-y-auto p-4' data-testid='batch-progress'>
          <p className='body-01 mb-3 text-gray-600'>
            {group.optimisation ? `${group.optimisation.mileage} miles · ${group.optimisation.duration} · ` : ''}
            {group.driver ? `With ${group.driver.fullName}` : 'Not with a driver yet'}
          </p>

          {/* The same map the batch was agreed on, so the office is looking at
              what the driver was given rather than at a list of place names. */}
          <div className='mb-4'>
            <TransportRunMap stops={group.stops} />
          </div>

          {!jobs && (
            <div className='flex justify-center py-8'>
              <Spinner size={30} />
            </div>
          )}

          {jobs && jobs.length === 0 && (
            <p className='body-01 text-gray-600'>The jobs in this batch are no longer there.</p>
          )}

          <div className='space-y-2'>
            {jobs?.map((job) => (
              <button
                key={job.uid}
                type='button'
                data-testid='batch-job'
                onClick={() => openJob(job.uid)}
                className='flex w-full items-start justify-between gap-3 rounded border p-3 text-left hover:border-blue-400'
              >
                <div>
                  <p className='body-02 font-semibold'>
                    {/* What the requester quotes on the phone, first, because
                        that is the direction the question comes from. */}
                    {job.requestReference && (
                      <span className='mr-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-700'>
                        {job.requestReference}
                      </span>
                    )}
                    {job.requestType ?? 'Transport job'} — {job.details?.name}
                  </p>
                  <p className='body-01 text-gray-600'>
                    {job.collectionSite ?? job.details?.site} → {job.destinationSite}
                  </p>
                  {job.details?.collectionPoint && (
                    <p className='body-01 text-gray-500'>{job.details.collectionPoint}</p>
                  )}
                </div>
                <Lozenge>{TransportJobStatusLabels[job.status] ?? job.status}</Lozenge>
              </button>
            ))}
          </div>

          {/* The plan, underneath what has happened to it. */}
          <p className='body-02 mt-5 mb-2 font-semibold'>The route</p>
          <div className='space-y-2'>
            {group.stops.map((stop) => (
              <div key={stop.sequence} data-testid='run-stop' className='flex gap-2 rounded border p-3'>
                <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xs font-semibold text-white'>
                  {stop.sequence}
                </span>
                <div>
                  <p className='body-02 font-semibold'>{stop.site}</p>
                  {stop.point && <p className='body-01 text-gray-600'>{stop.point}</p>}
                  {/* A stop is a place, and a place can be both at once — one
                      thing dropped off while another is picked up. Which is
                      which is on the item. */}
                  {stop.items.map((item) => (
                    <p key={item.jobUid} className='body-01 mt-1'>
                      <span className={item.type === 'collection' ? 'text-blue-700' : 'text-green-700'}>
                        {item.type === 'collection' ? 'Collect' : 'Deliver'}
                      </span>{' '}
                      — {item.requestType}, {item.requester}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
};

export default BatchProgressDialog;
